import React, { useEffect, useState, useRef } from "react";
import { Card, CardContent } from "../ui/card";
import BoatImg from "../../assets/BARCO.svg"; // SVG do barco

export default function TimelineSection({ timeline = [] }) {
  const [visibleItems, setVisibleItems] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [vehiclePos, setVehiclePos] = useState({ left: 0, top: 0 });

  const sectionRef = useRef(null);
  const trackWrapRef = useRef(null);
  const trackRef = useRef(null);           // “canal” central
  const trackCenterRef = useRef(null);     // linha central p/ centro exato (desktop)
  const vehicleRef = useRef(null);
  const cardRefs = useRef([]);

  // Detecta mobile
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Lógica de scroll + posição do barco
  useEffect(() => {
    cardRefs.current = timeline.map(() => React.createRef());

    const update = () => {
      if (!sectionRef.current || !trackWrapRef.current || !trackRef.current || !vehicleRef.current || !trackCenterRef.current) {
        return;
      }

      // Visibilidade progressiva
      const sec = sectionRef.current.getBoundingClientRect();
      const secTop = sec.top + window.scrollY;
      const secH = sec.height;
      const vh = window.innerHeight;
      const secP = Math.max(
        0,
        Math.min(
          1,
          (window.scrollY - (secTop - vh + 200)) /
            (secTop + secH - 200 - (secTop - vh + 200))
        )
      );
      const newVisible = new Set();
      const itemsToShow = Math.floor(secP * timeline.length * 1.2);
      for (let i = 0; i < Math.min(itemsToShow, timeline.length); i++) newVisible.add(i);
      setVisibleItems(newVisible);

      // Posição presa ao “canal”
      const wrapRect = trackWrapRef.current.getBoundingClientRect();
      const trackRect = trackRef.current.getBoundingClientRect();
      const centerRect = trackCenterRef.current.getBoundingClientRect();

      const vehicleH = vehicleRef.current.offsetHeight || 0;

      const trackTopGlobal = trackRect.top + window.scrollY;
      const centerOfViewport = window.scrollY + vh / 2;
      let p = (centerOfViewport - trackTopGlobal) / trackRect.height;
      p = Math.max(0, Math.min(1, p));

      const trackTopInWrap = trackRect.top - wrapRect.top;
      const usableH = Math.max(0, trackRect.height - vehicleH);

      const MOBILE_OFFSET_Y = 180;
      const extraY = isMobile ? MOBILE_OFFSET_Y : 0;

      let topInWrap = trackTopInWrap + usableH * p + extraY;

      // clamp
      const minTop = trackTopInWrap;
      const maxTop = trackTopInWrap + usableH;
      topInWrap = Math.max(minTop, Math.min(maxTop, topInWrap));

      if (isMobile) {
        setVehiclePos(prev => ({ left: prev.left, top: Math.round(topInWrap) }));
      } else {
        const centerXInWrap = centerRect.left - wrapRect.left;
        setVehiclePos({ left: Math.round(centerXInWrap), top: Math.round(topInWrap) });
      }
    };

    const onScroll = () => update();
    const onResize = () => update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    requestAnimationFrame(update);

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, [timeline, isMobile]);

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-blue-50 via-white to-cyan-50 relative overflow-x-clip"
    >
      {/* Animations & water helpers */}
      <style>{`
        @keyframes boat-bob {
          0%   { transform: translate(-50%, 0px) }
          50%  { transform: translate(-50%, -4px) }
          100% { transform: translate(-50%, 0px) }
        }
        .boat-bob {
          animation: boat-bob 2.8s ease-in-out infinite;
        }

        /* brilho/fluxo vertical da água */
        @keyframes water-flow {
          0%   { background-position: 0 0, 0 0, 0 0; }
          100% { background-position: 0 600px, 0 400px, 0 300px; }
        }

        /* ondulações horizontais leves */
        @keyframes wave-drift {
          0%   { transform: translateX(0) }
          50%  { transform: translateX(8px) }
          100% { transform: translateX(0) }
        }

        .water-canal {
          /* gradiente base do canal */
          background:
            /* brilho vertical (movendo) */
            linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 35%, rgba(255,255,255,0.18) 70%, rgba(255,255,255,0.06) 100%),
            /* véu translúcido */
            linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.02)),
            /* azul/ciano base */
            linear-gradient(180deg, #a7e3f2 0%, #bcdcf6 40%, #aee6ef 100%);
          background-size: 100% 600px, 100% 400px, 100% 100%;
          animation: water-flow 14s linear infinite;
        }

        .water-texture {
          /* pontinhos/bokeh aquático */
          background-image:
            radial-gradient(circle at 20% 10%, rgba(255,255,255,0.20) 0 2px, transparent 3px),
            radial-gradient(circle at 80% 30%, rgba(255,255,255,0.18) 0 1.5px, transparent 3px),
            radial-gradient(circle at 30% 60%, rgba(255,255,255,0.16) 0 2px, transparent 3px),
            radial-gradient(circle at 70% 80%, rgba(255,255,255,0.14) 0 2px, transparent 3px);
          background-size: 180px 240px, 200px 260px, 160px 220px, 220px 280px;
          background-repeat: repeat;
          filter: blur(0.2px);
          opacity: 0.4;
          animation: water-flow 18s linear infinite;
        }

        .water-waves {
          background-image: repeating-linear-gradient(
            90deg,
            rgba(255,255,255,0.15) 0px,
            rgba(255,255,255,0.15) 2px,
            rgba(255,255,255,0.00) 10px
          );
          opacity: 0.25;
          animation: wave-drift 6s ease-in-out infinite;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-24">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Marcos do Transporte Aquaviário 2024
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Acompanhe a jornada dos principais acontecimentos do setor aquaviário brasileiro
          </p>
        </div>

        <div ref={trackWrapRef} className="relative">
          {/* Canal central com camadas de água */}
          <div
            ref={trackRef}
            className="absolute left-1/2 top-0 bottom-0 -translate-x-1/2 w-48 rounded-full shadow-inner z-10 overflow-hidden"
          >
            {/* camada base “água” */}
            <div className="absolute inset-0 water-canal" />
            {/* textura (bolhas/sparkles) */}
            <div className="absolute inset-0 water-texture" />
            {/* ondas finas horizontais */}
            <div className="absolute inset-0 water-waves" />
            {/* linha de centro invisível p/ cálculo */}
            <div
              ref={trackCenterRef}
              className="absolute left-1/2 top-0 w-px h-full pointer-events-none"
            />
          </div>

          {/* Barco (SVG importado), centralizado no canal, com balanço suave */}
          <div
            ref={vehicleRef}
            className={`${isMobile ? "z-10" : "z-20"} absolute transition-[top,left] duration-150 ease-out select-none pointer-events-none`}
            style={{
              left: isMobile ? "50%" : `${vehiclePos.left}px`,
              top: `${vehiclePos.top}px`,
            }}
          >
            <div className="boat-bob -translate-x-1/2">
              <img
                src={BoatImg}
                alt="Barco"
                className="w-24 md:w-28 h-auto drop-shadow-xl"
                draggable={false}
              />
            </div>
          </div>

          {/* Itens da timeline */}
          <div
            className={`${isMobile ? "space-y-0" : "space-y-4"} relative ${isMobile ? "z-20" : ""}`}
          >
            {timeline.map((item, index) => {
              const isLeft = index % 2 === 0;
              const isVisible = visibleItems.has(index);

              if (isMobile) {
                // Mobile: card centralizado, um por “tela”
                return (
                  <div key={index} className="min-h-[80vh] flex items-center justify-center">
                    <div
                      ref={cardRefs.current[index]}
                      className={`w-[90%] max-w-md transition-all duration-500 ease-out ${
                        isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
                      }`}
                    >
                      <div className="relative">
                        {/* Conector curto até o canal */}
                        <div className="absolute left-1/2 -translate-x-1/2 -top-8 w-0.5 h-7 bg-gradient-to-b from-cyan-300 to-blue-400" />
                        <Card className="shadow-xl border-0 bg-white/85 backdrop-blur-sm">
                          <CardContent className="p-6">
                            <div className="flex items-start gap-4">
                              <div className="flex-shrink-0">
                                <div className="w-14 h-14 bg-gradient-to-br from-blue-100 to-cyan-200 rounded-xl flex items-center justify-center text-3xl shadow border border-blue-200">
                                  {item.icon}
                                </div>
                              </div>
                              <div className="flex-1 text-left">
                                <span className="bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-3 py-1.5 rounded-full text-sm font-semibold shadow inline-block mb-3">
                                  {item.month}
                                </span>
                                <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">
                                  {item.title}
                                </h3>
                                <p className="text-gray-700 leading-relaxed">
                                  {item.description}
                                </p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </div>
                    </div>
                  </div>
                );
              }

              // Desktop
              return (
                <div
                  key={index}
                  className={`flex items-center min-h-[250px] ${isLeft ? "justify-start" : "justify-end"}`}
                >
                  <div
                    ref={cardRefs.current[index]}
                    className={`w-1/2 transition-all duration-700 ease-in-out ${
                      isVisible
                        ? "opacity-100 translate-x-0"
                        : `opacity-0 ${isLeft ? "-translate-x-10" : "translate-x-10"}`
                    }`}
                  >
                    <div className={`relative ${isLeft ? "pr-28" : "pl-28"}`}>
                      {/* Conector ao canal */}
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 h-0.5 bg-gradient-to-r ${
                          isLeft
                            ? "right-0 w-28 from-cyan-300 to-blue-500"
                            : "left-0 w-28 from-blue-500 to-cyan-300"
                        }`}
                      />
                      <div
                        className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full border-2 border-blue-400 shadow-lg ${
                          isLeft ? "right-24" : "left-24"
                        }`}
                      />

                      <Card className="hover:shadow-2xl transition-all duration-300 border-0 shadow-lg bg-white/95 backdrop-blur-sm">
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            <div className="flex-shrink-0">
                              <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-200 rounded-xl flex items-center justify-center text-3xl shadow-lg border border-blue-200">
                                {item.icon}
                              </div>
                            </div>
                            <div className="flex-1 text-left">
                              <span className="bg-gradient-to-r from-blue-600 to-cyan-700 text-white px-4 py-1.5 rounded-full text-sm font-semibold shadow-lg inline-block mb-3">
                                {item.month}
                              </span>
                              <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                                {item.title}
                              </h3>
                              <p className="text-gray-600 leading-relaxed">
                                {item.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </div>
                </div>
              );
            })}

            {timeline.length === 0 && (
              <div className="py-16 text-center text-gray-500">
                Nenhum item da timeline disponível.
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
